import clsx from 'clsx';
import isNil from 'lodash/isNil';
import { useMemo, useRef } from 'react';

import AIText from '../../AIText/AIText';
import { MonitoringStatus } from '../../../store/investor/constants';
import { useAppSelector } from '../../../app/hooks';
import { useOutsideClick } from '../../../utils/constants/hooks';
import { Mixpanel } from '../../../utils/mixpanel';
import { AICA_CREATE_DEAL } from '../../../utils/constants/mixpanelEvents/investorEvents';

import { CREATE_DEAL, MANAGE_USERS } from '../../../utils/constants/commonConstants';

import threeDotVertical from '../../../assets/three-dot-vertical.svg';

import styles from './ActionButton.module.scss';

type ActionButtonProps = {
    rowIndex: number;
    row: any;
    handleActionClick: (action: string, row: any) => void;
    isOpen: boolean;
    onToggleDropdown: () => void;
};

function ActionButton({ rowIndex, row, handleActionClick, isOpen, onToggleDropdown }: ActionButtonProps) {
    const secondaryTriggers = row?.action?.secondCTATrigger || [];
    const user = useAppSelector((state) => state.user);
    const onePagerDataLoading = useAppSelector((state) => state.investor.onePager.loading);
    const ndaLoading = useAppSelector((state) => state.investor.nda.loading);
    const engagementLetterLoading = useAppSelector((state) => state.investor.engagementLetter.loading);
    const applicationFormLoading = useAppSelector((state) => state.investor.applicationForm.loading);

    const menuRef = useRef<null | HTMLDivElement>(null);
    const threeDotRef = useRef<null | HTMLImageElement>(null);

    useOutsideClick(menuRef, () => {
        if (isOpen) {
            onToggleDropdown();
        }
    });

    const isSummonUser = (() => {
        try {
            return !isNil(sessionStorage.getItem('auth_token'));
        } catch (error) {
            console.warn('SessionStorage access failed:', error);
            return false;
        }
    })();

    const toggleOptions = (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();
        onToggleDropdown();
    };

    const getButtonLabel = (action = '') => {
        switch (action) {
            case 'TOGGLE_MONITORING': {
                return (
                    <div className={styles.MonitoringWrap}>
                        <span>Turn Monitoring: {row?.monitoringStatus === 'ACTIVE' ? 'OFF' : 'ON'}</span>
                        <div
                            className={clsx(styles.MonitoringDot, {
                                [styles.Green]: row?.monitoringStatus === MonitoringStatus.INACTIVE,
                                [styles.Red]: row?.monitoringStatus === MonitoringStatus.ACTIVE,
                            })}
                        />
                    </div>
                );
            }
            case 'VIEW_ONE_PAGER':
                return onePagerDataLoading ? (
                    <AIText text="Please Wait" showLoadingDots={true} style={{ fontFamily: 'inherit' }} />
                ) : (
                    'View One Pager'
                );
            case 'VIEW_ENGAGEMENT_LETTER':
                return engagementLetterLoading ? (
                    <AIText text="Please Wait" showLoadingDots={true} style={{ fontFamily: 'inherit' }} />
                ) : (
                    'View Engagement Letter'
                );
            case 'VIEW_NDA':
                return ndaLoading ? <AIText text="Please Wait" showLoadingDots={true} style={{ fontFamily: 'inherit' }} /> : 'View NDA';
            case 'VIEW_APPLICATION_FORM':
                return applicationFormLoading ? (
                    <AIText text="Please Wait" showLoadingDots={true} style={{ fontFamily: 'inherit' }} />
                ) : (
                    'View Application Form'
                );
            case 'VIEW_BUREAU_KYC_CONSENT':
                return 'View Bureau & KYC Consent';
            case 'VIEW_REPORT':
                return 'View Report';
            case 'CREATE_DEAL':
                return 'Create Deal';
            case 'UPLOAD_DATA':
                return 'View Account';
            case 'DOWNLOAD_RAW':
                return 'Export Data';
            case 'UNLOCK_REPORT':
                return 'Unlock AICA Report';
            case 'INVITE_USER':
                return 'Invite User';
            case MANAGE_USERS:
                return 'Manage Users';
            case 'SUMMON':
                return 'View Account';
            case 'ADD_TO_PORTFOLIO':
                return 'Add To Portfolio';
            case 'DOWNLOAD_IR':
                return 'Export Investment Report';
            case 'VIEW_DATA_ROOM':
                return 'View Dataroom';
            case 'GENERATE_PDF_REPORT':
                return 'Generate PDF Report';
            case 'EDIT_PROFILE':
                return 'Edit Profile';
            case 'COMPOSE_EMAIL':
                return 'Compose Email';
            case 'ARCHIVE_LEAD':
                return 'Archive Lead';
            case 'UNARCHIVE_LEAD':
                return 'Unarchive Lead';
            default:
                return null;
        }
    };

    const trackMixpanelEvent = (row) => {
        const mixpanelData = {
            Company_Org_Id: row?.assesseeOrgId,
            PartnetAccessType: user?.userData?.accountType,
            Dsa_Id: user?.userData?.investorOrgId,
            websiteUrl: window.location.href,
        };
        Mixpanel.track(AICA_CREATE_DEAL, mixpanelData);
    };

    const filteredSecondaryTriggers = useMemo(() => {
        if (isSummonUser) {
            return secondaryTriggers?.filter((trigger: string) => trigger !== 'SUMMON') ?? [];
        }
        if (user?.userData?.accountType === 'PARTNER') {
            return secondaryTriggers?.filter((trigger: string) => trigger !== 'COMPOSE_EMAIL') ?? [];
        }

        return secondaryTriggers;
    }, [secondaryTriggers, isSummonUser]);

    return (
        <div className={styles.Action}>
            {!!filteredSecondaryTriggers.length && (
                <div className={styles.ActionContainer}>
                    <div
                        className={clsx(styles.ThreeDotBox, {
                            [styles.ThreeDotActive]: isOpen,
                        })}
                    >
                        <img
                            id={rowIndex.toString()}
                            src={threeDotVertical}
                            alt="upl"
                            height="30px"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleOptions(e);
                            }}
                            ref={threeDotRef}
                        />
                    </div>

                    <div ref={menuRef} className={clsx(styles.Options, { [styles.Active]: isOpen })}>
                        {filteredSecondaryTriggers.map((trigger, index) => {
                            const label = getButtonLabel(trigger);
                            if (!label) return null;

                            return (
                                <div
                                    key={index}
                                    className={styles.Option}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleActionClick(trigger, row);
                                        if (trigger === CREATE_DEAL) {
                                            trackMixpanelEvent(row);
                                        }
                                    }}
                                >
                                    {label}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ActionButton;
